import type { ReactNode } from "react";

import { usingOrbitIdentity } from "@/api/client";
import { AphelionLogo } from "@/components/AphelionLogo";
import { ThemeToggle } from "@/components/ThemeToggle";

/** The chrome every signed-OUT page wears — the login card, minus the form.
 *
 *  These pages are reached by someone who is NOT signed in and often does not
 *  yet have an account: a reset link from their inbox, a verification link, an
 *  emailed tool download. They are the product's front door for a person who
 *  has never seen it, so they get the same card as the login page rather than
 *  a bare white page with a sentence on it.
 */
export function PublicShell({ eyebrow, title, sub, children }: {
  eyebrow: string;
  title: string;
  sub?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="login-wrap">
      <div style={{ position: "fixed", top: 14, right: 14, zIndex: 20 }}>
        <ThemeToggle />
      </div>
      <div className="login-card">
        <div className="login-branding">
          <AphelionLogo width={210} height={136} animated />
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <span className="brand-name">Aphelion Energy</span>
            <span className="brand-tagline">Future of Energy; Engineered!</span>
            <span className="brand-version">Orbit v0.1.0</span>
          </div>
        </div>
        <div className="login-div" />
        <div className="login-form-side">
          <div className="l-eyebrow">{eyebrow}</div>
          <h2 className="l-title">{title}</h2>
          {sub && <p className="l-sub">{sub}</p>}
          {children}
          {/* Follows the backend, like the login page's — a hardcoded string is
              how a footer ends up crediting a system retired months earlier. */}
          <div className="lsep">
            <span>{usingOrbitIdentity() ? "Secured by Orbit" : "Secured by Fabric"}</span>
          </div>
          <div className="srvrow"><span className="gdot" /> orbit.aphelionenergy.com</div>
        </div>
      </div>
    </div>
  );
}

/** One error line, in the place and colour the login page puts it. */
export function FormError({ children }: { children: ReactNode }) {
  return (
    <div style={{ fontSize: 11.5, color: "var(--bad)", marginBottom: 12, textAlign: "center" }}>
      {children}
    </div>
  );
}
